import React from 'react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Edit, Save, Trash2, User, X } from 'lucide-react';
import { Player } from '@/types/player';

interface PlayerHeaderProps {
  player: Player | null;
  playerName: string;
  isEditing: boolean;
  isUpdating: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
}

export const PlayerHeader = ({
  player,
  playerName,
  isEditing,
  isUpdating,
  onEdit,
  onSave,
  onCancel,
  onDelete
}: PlayerHeaderProps) => {
  return (
    <div className="flex items-center justify-between mb-8">
      <div className="flex items-center gap-4">
        <Avatar className="h-16 w-16">
          <AvatarImage src={player?.profile_picture_url} alt={playerName} />
          <AvatarFallback>
            <User className="h-8 w-8" />
          </AvatarFallback>
        </Avatar>
        <div>
          <h1 className="text-3xl font-bold">{playerName}</h1>
          <p className="text-muted-foreground">
            {player?.teams?.name && `${player.teams.name} • `}
            {player?.position || 'No position assigned'}
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        {isEditing ? (
          <>
            <Button onClick={onSave} disabled={isUpdating}>
              <Save className="h-4 w-4 mr-2" />
              {isUpdating ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </>
        ) : (
          <Button onClick={onEdit}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Player
          </Button>
        )}
        <Button variant="destructive" onClick={onDelete}>
          <Trash2 className="h-4 w-4 mr-2" />
          Delete
        </Button>
        <Button asChild variant="outline">
          <Link to={player?.teams?.id ? `/superadmin/team/${player.teams.id}` : '/superadmin'}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Team
          </Link>
        </Button>
      </div>
    </div>
  );
};